import React, { useEffect, useState, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { auth } from '../firebaseClient';
import { CustomerContext } from '../App';
import LoanOverview from './LoanOverview';
import StackedAreaChart from './StackedAreaChart';
import './Dashboard.css';

function formatEuro(num) {
  const val = parseFloat(num || 0);
  return '€' + val.toLocaleString();
}

function getYearOnly(dateStr) {
  if (!dateStr) return 'n/a';
  const d = new Date(dateStr);
  if (!isNaN(d.getTime())) {
    return d.getFullYear().toString();
  }
  return dateStr.slice(-4);
}

const months2025 = [
  'Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun',
  'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'
];

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

/** Kürzt die Arrays auf das letzte nicht-0-Element */
function trimToLastNonZero(incomeArr, expensesArr) {
  let lastIndex = -1;
  const length = Math.min(incomeArr.length, expensesArr.length);
  for (let i = length - 1; i >= 0; i--) {
    if (incomeArr[i] !== 0 || expensesArr[i] !== 0) {
      lastIndex = i;
      break;
    }
  }
  if (lastIndex === -1) {
    return {
      trimmedIncome: [],
      trimmedExpenses: [],
      lastIncome: 0,
      lastExpenses: 0,
      trimmedMonths: []
    };
  }
  const trimmedIncome = incomeArr.slice(0, lastIndex + 1);
  const trimmedExpenses = expensesArr.slice(0, lastIndex + 1);
  const lastIncome = trimmedIncome[trimmedIncome.length - 1] || 0;
  const lastExpenses = trimmedExpenses[trimmedExpenses.length - 1] || 0;
  const trimmedMonths = months2025.slice(0, lastIndex + 1);

  return {
    trimmedIncome,
    trimmedExpenses,
    lastIncome,
    lastExpenses,
    trimmedMonths
  };
}

/** Kredite transformieren + filtern */
function transformCreditData(data, selectedCustomerId) {
  if (!Array.isArray(data)) return [];
  const filtered = data.filter(row => row.customerid === selectedCustomerId);
  return filtered.map((row) => {
    const propertyIds = (row.propertyid || '')
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
    const objectIds = (row.objectid || '')
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
    const interestStr = (row.interestrate || '')
      .replace('%', '')
      .replace(',', '.');
    const address = `${row.street || ''} ${row.number || ''}, ${row.zipcode || ''} ${row.city || ''}`.trim();

    return {
      creditId: row.creditid,
      propertyIds,
      objectIds,
      propertyName: address,
      payment: parseFloat((row.monthlypayment || '0').replace(',', '.')) || 0,
      interestRate: parseFloat(interestStr) || 0,
      remainingCredit: parseFloat((row.remainingcredit || '0').replace(',', '.')) || 0,
      duration: parseFloat((row.duration || '0').replace(',', '.')) || 0,
      yearsRemaining: parseFloat((row.yearsremaining || '0').replace(',', '.')) || 0,
    };
  });
}

/** Objekt-Daten transformieren */
function transformObjectData(dashboardObjData, overviewData, selectedCustomerId) {
  if (!Array.isArray(dashboardObjData)) return [];
  const filteredDashboard = dashboardObjData.filter(row => row.customerid === selectedCustomerId);
  const overviewMap = new Map(
    (overviewData || [])
      .filter(ov => ov.customerid === selectedCustomerId)
      .map(item => [item.objectid, item])
  );

  return filteredDashboard.map((row) => {
    const parseVal = v => parseFloat((v || '0').replace(',', '.')) || 0;
    const objectId = row.objectid;
    const overviewItem = overviewMap.get(objectId) || {};

    // Income + Expenses
    const incomeRaw = [
      row.objectincome012025, row.objectincome022025, row.objectincome032025,
      row.objectincome042025, row.objectincome052025, row.objectincome062025,
      row.objectincome072025, row.objectincome082025, row.objectincome092025,
      row.objectincome102025, row.objectincome112025, row.objectincome122025
    ].map(parseVal);
    const expensesRaw = [
      row.objectexpenses012025, row.objectexpenses022025, row.objectexpenses032025,
      row.objectexpenses042025, row.objectexpenses052025, row.objectexpenses062025,
      row.objectexpenses072025, row.objectexpenses082025, row.objectexpenses092025,
      row.objectexpenses102025, row.objectexpenses112025, row.objectexpenses122025
    ].map(parseVal);

    const { trimmedIncome, trimmedExpenses, lastIncome, lastExpenses, trimmedMonths } =
      trimToLastNonZero(incomeRaw, expensesRaw);

    return {
      objectId,
      bezeichnung: overviewItem.bezeichnungimberblick || '',
      street: overviewItem.street || '',
      number: overviewItem.number || '',
      city: overviewItem.city || '',
      income: trimmedIncome,
      expenses: trimmedExpenses,
      monthsUsed: trimmedMonths,
      lastMonthIncome: lastIncome,
      lastMonthExpenses: lastExpenses,
      objectValue: parseVal(row.objectvalue),
      purchasingValue: parseVal(row.purchasingvalue),
      occupancyRate: row.occupancyrate || 'n/a',
      numberOfUnits: parseInt(overviewItem.numberofunits || '0', 10),
      objectPurchasingDate: row.objectpurchasingdate || '',
      objectValuationDate: row.objectvaluationdate || '',
      objectValueChangeIn: row.objectvaluechangein || '0%',
    };
  });
}

/** Einheiten (Properties) transformieren */
function transformSinglePropertyData(dashboardPropData, overviewData, selectedCustomerId) {
  if (!Array.isArray(dashboardPropData)) return [];
  const filteredDashboard = dashboardPropData.filter(row => row.customerid === selectedCustomerId);
  const overviewMap = new Map(
    (overviewData || [])
      .filter(ov => ov.customerid === selectedCustomerId)
      .map(item => [item.propertyid, item])
  );

  return filteredDashboard.map((row) => {
    const parseVal = v => parseFloat((v || '0').replace(',', '.')) || 0;
    const propertyId = row.propertyid;
    const overviewItem = overviewMap.get(propertyId) || {};

    // Income + Expenses
    const incomeRaw = [
      row.propertyincome012025, row.propertyincome022025, row.propertyincome032025,
      row.propertyincome042025, row.propertyincome052025, row.propertyincome062025,
      row.propertyincome072025, row.propertyincome082025, row.propertyincome092025,
      row.propertyincome102025, row.propertyincome112025, row.propertyincome122025
    ].map(parseVal);
    const expensesRaw = [
      row.propertyexpenses012025, row.propertyexpenses022025, row.propertyexpenses032025,
      row.propertyexpenses042025, row.propertyexpenses052025, row.propertyexpenses062025,
      row.propertyexpenses072025, row.propertyexpenses082025, row.propertyexpenses092025,
      row.propertyexpenses102025, row.propertyexpenses112025, row.propertyexpenses122025
    ].map(parseVal);

    const { trimmedIncome, trimmedExpenses, lastIncome, lastExpenses, trimmedMonths } =
      trimToLastNonZero(incomeRaw, expensesRaw);

    return {
      propertyId,
      bezeichnung: overviewItem.bezeichnungimberblick || '',
      street: overviewItem.street || '',
      number: overviewItem.number || '',
      city: overviewItem.city || '',
      income: trimmedIncome,
      expenses: trimmedExpenses,
      monthsUsed: trimmedMonths,
      lastMonthIncome: lastIncome,
      lastMonthExpenses: lastExpenses,
      fullYearIncome: parseVal(row.propertyincomefullyear),
      purchasingValue: parseVal(row.purchasingvalue),
      value: parseVal(row.propertyvalue),
      purchasingDate: row.purchasingdate || '',
      valuationDate: row.valuationdate || '',
      propertyValueChangeIn: row.propertyvaluechangein || '0%',
      vermietungsrate: row.occupancyrate || 'n/a',
    };
  });
}

/** Portfolio transformieren */
function transformPortfolioData(data, selectedCustomerId) {
  if (!Array.isArray(data) || !data.length) return null;
  const filtered = data.filter(row => row.customerid === selectedCustomerId);
  if (!filtered.length) return null;

  const row = filtered[0];
  const parseVal = v => parseFloat((v || '0').replace(',', '.')) || 0;

  // 12 Monate Income
  const incomeRaw = [
    row.portfolioincome012025, row.portfolioincome022025, row.portfolioincome032025,
    row.portfolioincome042025, row.portfolioincome052025, row.portfolioincome062025,
    row.portfolioincome072025, row.portfolioincome082025, row.portfolioincome092025,
    row.portfolioincome102025, row.portfolioincome112025, row.portfolioincome122025
  ].map(parseVal);
  // 12 Monate Expenses
  const expensesRaw = [
    row.portfolioexpenses012025, row.portfolioexpenses022025, row.portfolioexpenses032025,
    row.portfolioexpenses042025, row.portfolioexpenses052025, row.portfolioexpenses062025,
    row.portfolioexpenses072025, row.portfolioexpenses082025, row.portfolioexpenses092025,
    row.portfolioexpenses102025, row.portfolioexpenses112025, row.portfolioexpenses122025
  ].map(parseVal);

  const { trimmedIncome, trimmedExpenses, lastIncome, lastExpenses, trimmedMonths } =
    trimToLastNonZero(incomeRaw, expensesRaw);

  return {
    currentValue: parseVal(row.portfoliovalue),
    purchasingValue: parseVal(row.portfoliopurchasingvalue),
    valueChangeIn: row.portfoliovaluechangein || '0%',
    occupancyRate: row.occupancyrate || 'n/a',
    purchasingDate: row.avepurchasingdate || '',
    valuationDate: row.valuationdate || '',
    numberOfProperties: parseInt(row.numberofproperties || '0', 10),
    income: trimmedIncome,
    expenses: trimmedExpenses,
    monthsUsed: trimmedMonths,
    lastMonthIncome: lastIncome,
    lastMonthExpenses: lastExpenses
  };
}

function Dashboard() {
  const { selectedCustomerId, setSelectedCustomerId } = useContext(CustomerContext);
  const location = useLocation();
  const isDemo = location.pathname.startsWith('/demo');

  // --- Evtl. ID aus Firebase-Auth laden (nur einmalig) ---
  // ABER NICHT im Demo-Modus, da dort die ID bereits von DemoRoute gesetzt wird
  useEffect(() => {
    // Im Demo-Modus nicht die CustomerID aus Firebase laden
    if (isDemo) return;
    
    async function fetchCustomerId() {
      const user = auth.currentUser;
      if (!user) return;
      const email = user.email;
      try {
        const resp = await fetch(`${API_URL}/api/customerIdByEmail?email=${encodeURIComponent(email)}`);
        if (resp.ok) {
          const json = await resp.json();
          if (json.customerId) {
            setSelectedCustomerId(json.customerId);
          }
        }
      } catch (err) {
        console.error('Fehler:', err);
      }
    }
    fetchCustomerId();
  }, [setSelectedCustomerId, isDemo]);

  // Lokale States
  const [currentView, setCurrentView] = useState('portfolio');
  // Raw Data
  const [rawPortfolioData, setRawPortfolioData] = useState([]);
  const [rawObjectData, setRawObjectData] = useState([]);
  const [rawSingleData, setRawSingleData] = useState([]);
  const [allLoans, setAllLoans] = useState([]);
  const [objectOverviewData, setObjectOverviewData] = useState([]);
  const [singleOverviewData, setSingleOverviewData] = useState([]);

  // Verarbeitete Daten
  const [portfolioData, setPortfolioData] = useState(null);
  const [objectData, setObjectData] = useState([]);
  const [singlePropertyData, setSinglePropertyData] = useState([]);

  // Objekt-States
  const [selectedObjectId, setSelectedObjectId] = useState(null);
  const [objectValue, setObjectValue] = useState(0);
  const [objectPurchasingValue, setObjectPurchasingValue] = useState(0);
  const [objectPurchasingDate, setObjectPurchasingDate] = useState('');
  const [objectValuationDate, setObjectValuationDate] = useState('');
  const [objectValueChangeIn, setObjectValueChangeIn] = useState('0%');
  const [objectLastMonthIncome, setObjectLastMonthIncome] = useState(0);
  const [objectLastMonthExpenses, setObjectLastMonthExpenses] = useState(0);
  const [objectOccupancyRate, setObjectOccupancyRate] = useState('n/a');
  const [objectUnits, setObjectUnits] = useState(0);
  const [objectCashflowData, setObjectCashflowData] = useState([]);
  const [filteredObjectLoans, setFilteredObjectLoans] = useState([]);

  // Einheiten-States
  const [selectedSinglePropertyId, setSelectedSinglePropertyId] = useState(null);
  const [singleValue, setSingleValue] = useState(0);
  const [singlePurchasingValue, setSinglePurchasingValue] = useState(0);
  const [singlePDate, setSinglePDate] = useState('');
  const [singleVDate, setSingleVDate] = useState('');
  const [singleValueChangeIn, setSingleValueChangeIn] = useState('0%');
  const [singleFullYearIncome, setSingleFullYearIncome] = useState(0);
  const [singleLastMonthIncome, setSingleLastMonthIncome] = useState(0);
  const [singleLastMonthExpenses, setSingleLastMonthExpenses] = useState(0);
  const [singleOccupancy, setSingleOccupancy] = useState('n/a');
  const [cashflowData, setCashflowData] = useState([]);
  const [filteredLoans, setFilteredLoans] = useState([]);

  // Portfolio-States
  const [totalValue, setTotalValue] = useState(0);
  const [vermietungsrate, setVermietungsrate] = useState('n/a');
  const [portfolioCashflow, setPortfolioCashflow] = useState([]);
  const [portfolioPurchasingValue, setPortfolioPurchasingValue] = useState(0);
  const [portfolioValue, setPortfolioValue] = useState(0);
  const [portfolioValueChangeIn, setPortfolioValueChangeIn] = useState('0%');
  const [avePurchasingDate, setAvePurchasingDate] = useState('');
  const [valuationDate, setValuationDate] = useState('');
  const [lastMonthIncome, setLastMonthIncome] = useState(0);
  const [lastMonthExpenses, setLastMonthExpenses] = useState(0);
  const [portfolioNumUnits, setPortfolioNumUnits] = useState(0);

  // --- Daten einmalig laden (für alle Customer) ---
  useEffect(() => {
    fetch(`${API_URL}/api/dashboard-portfolio`)
      .then(res => res.json())
      .then(rows => setRawPortfolioData(rows))
      .catch(err => console.error(err));

    fetch(`${API_URL}/api/object-overview`)
      .then(res => res.json())
      .then(data => setObjectOverviewData(data))
      .catch(err => console.error(err));

    fetch(`${API_URL}/api/dashboard-object`)
      .then(res => res.json())
      .then(data => setRawObjectData(data))
      .catch(err => console.error(err));

    fetch(`${API_URL}/api/property-overview`)
      .then(res => res.json())
      .then(data => setSingleOverviewData(data))
      .catch(err => console.error(err));

    fetch(`${API_URL}/api/dashboard-property`)
      .then(res => res.json())
      .then(data => setRawSingleData(data))
      .catch(err => console.error(err));

    fetch(`${API_URL}/api/credit-overview`)
      .then(res => res.json())
      .then(loanData => setAllLoans(loanData))
      .catch(err => console.error(err));
  }, []);

  // --- 1) Portfolio transformieren (abhängig von selectedCustomerId) ---
  useEffect(() => {
    if (!selectedCustomerId) {
      setPortfolioData(null);
      return;
    }
    const transformed = transformPortfolioData(rawPortfolioData, selectedCustomerId);
    setPortfolioData(transformed);
  }, [selectedCustomerId, rawPortfolioData]);

  // Wenn portfolioData existiert -> setze State-Felder
  useEffect(() => {
    if (!portfolioData) return;

    setPortfolioValue(portfolioData.currentValue || 0);
    setPortfolioPurchasingValue(portfolioData.purchasingValue || 0);
    setPortfolioValueChangeIn(portfolioData.valueChangeIn || '0%');
    setVermietungsrate(portfolioData.occupancyRate || 'n/a');
    setAvePurchasingDate(portfolioData.purchasingDate || '');
    setValuationDate(portfolioData.valuationDate || '');
    setLastMonthIncome(portfolioData.lastMonthIncome || 0);
    setLastMonthExpenses(portfolioData.lastMonthExpenses || 0);
    setPortfolioNumUnits(portfolioData.numberOfProperties || 0);
    setTotalValue(portfolioData.currentValue || 0);

    // Cashflow-Array
    const cf = portfolioData.income.map((val, idx) => ({
      month: portfolioData.monthsUsed[idx],
      income: val,
      expenses: portfolioData.expenses[idx]
    }));
    setPortfolioCashflow(cf);
  }, [portfolioData]);

  // --- 2) Objekte transformieren (abhängig von selectedCustomerId) ---
  useEffect(() => {
    if (!selectedCustomerId) {
      setObjectData([]);
      return;
    }
    if (!rawObjectData.length || !objectOverviewData.length) return;

    const merged = transformObjectData(rawObjectData, objectOverviewData, selectedCustomerId);
    setObjectData(merged);

    if (merged.length > 0) {
      setSelectedObjectId(merged[0].objectId);
    }
  }, [selectedCustomerId, rawObjectData, objectOverviewData]);

  // Wenn selectedObjectId existiert -> Felder setzen
  useEffect(() => {
    if (!selectedObjectId || !objectData.length) return;
    const obj = objectData.find(o => o.objectId === selectedObjectId);
    if (!obj) return;

    setObjectValue(obj.objectValue || 0);
    setObjectPurchasingValue(obj.purchasingValue || 0);
    setObjectPurchasingDate(obj.objectPurchasingDate || '');
    setObjectValuationDate(obj.objectValuationDate || '');
    setObjectValueChangeIn(obj.objectValueChangeIn || '0%');
    setObjectLastMonthIncome(obj.lastMonthIncome || 0);
    setObjectLastMonthExpenses(obj.lastMonthExpenses || 0);
    setObjectOccupancyRate(obj.occupancyRate || 'n/a');
    setObjectUnits(obj.numberOfUnits || 0);

    // Cashflow
    const cf = obj.income.map((val, idx) => ({
      month: obj.monthsUsed[idx],
      income: val,
      expenses: obj.expenses[idx]
    }));
    setObjectCashflowData(cf);

    // Kredite filtern
    const allFilteredLoans = transformCreditData(allLoans, selectedCustomerId);
    const objLoans = allFilteredLoans.filter(loan =>
      Array.isArray(loan.objectIds) && loan.objectIds.includes(selectedObjectId)
    );
    setFilteredObjectLoans(objLoans);
  }, [selectedObjectId, objectData, allLoans, selectedCustomerId]);

  // --- 3) Einheiten transformieren (abhängig von selectedCustomerId) ---
  useEffect(() => {
    if (!selectedCustomerId) {
      setSinglePropertyData([]);
      return;
    }
    if (!rawSingleData.length || !singleOverviewData.length) return;

    const merged = transformSinglePropertyData(rawSingleData, singleOverviewData, selectedCustomerId);
    setSinglePropertyData(merged);

    if (merged.length > 0) {
      setSelectedSinglePropertyId(merged[0].propertyId);
    }
  }, [selectedCustomerId, rawSingleData, singleOverviewData]);

  // Wenn selectedSinglePropertyId existiert -> Felder setzen
  useEffect(() => {
    if (!selectedSinglePropertyId || !singlePropertyData.length) return;
    const sp = singlePropertyData.find(p => p.propertyId === selectedSinglePropertyId);
    if (!sp) return;

    setSingleValue(sp.value || 0);
    setSinglePurchasingValue(sp.purchasingValue || 0);
    setSinglePDate(sp.purchasingDate || '');
    setSingleVDate(sp.valuationDate || '');
    setSingleValueChangeIn(sp.propertyValueChangeIn || '0%');
    setSingleFullYearIncome(sp.fullYearIncome || 0);
    setSingleLastMonthIncome(sp.lastMonthIncome || 0);
    setSingleLastMonthExpenses(sp.lastMonthExpenses || 0);
    setSingleOccupancy(sp.vermietungsrate || 'n/a');

    // Cashflow
    const cf = sp.income.map((val, idx) => ({
      month: sp.monthsUsed[idx],
      income: val,
      expenses: sp.expenses[idx]
    }));
    setCashflowData(cf);

    // Kredite filtern
    const allFilteredLoans = transformCreditData(allLoans, selectedCustomerId);
    const propLoans = allFilteredLoans.filter(loan =>
      Array.isArray(loan.propertyIds) && loan.propertyIds.includes(selectedSinglePropertyId)
    );
    // Deduplicate
    const uniqueLoans = Array.from(new Map(propLoans.map(loan => [loan.creditId, loan])).values());
    setFilteredLoans(uniqueLoans);
  }, [selectedSinglePropertyId, singlePropertyData, allLoans, selectedCustomerId]);

  return (
    <div className="dashboard-container">
      <h1>Dashboard</h1>

      {/* Umschalten zwischen Portfolio, Objekt, Single */}
      <div className="view-buttons">
        <button
          className={`view-button ${currentView === 'portfolio' ? 'active' : ''}`}
          onClick={() => setCurrentView('portfolio')}
        >
          Portfolio-Ansicht
        </button>

        {objectData.length > 0 && (
          <button
            className={`view-button ${currentView === 'object' ? 'active' : ''}`}
            onClick={() => setCurrentView('object')}
          >
            Objekt-Ansicht
          </button>
        )}

        <button
          className={`view-button ${currentView === 'single' ? 'active' : ''}`}
          onClick={() => setCurrentView('single')}
        >
          Einheiten-Ansicht
        </button>
      </div>

      {/* === PORTFOLIO-ANSICHT === */}
      {currentView === 'portfolio' && portfolioData && (
        <section>
          <h2>Portfolio Übersicht</h2>
          <div className="overview-cards">
            <div className="overview-card">
              <h3>Gesamtwert</h3>
              <p>{formatEuro(totalValue.toFixed(0))}</p>
            </div>
            <div className="overview-card">
              <h3>Einnahmen letzter Monat</h3>
              <p>{formatEuro(lastMonthIncome.toFixed(0))}</p>
            </div>
            <div className="overview-card">
              <h3>Ausgaben letzer Monat</h3>
              <p>{formatEuro(lastMonthExpenses.toFixed(0))}</p>
            </div>
            <div className="overview-card">
              <h3>Vermietungsrate pro Jahr</h3>
              <p>{vermietungsrate}</p>
            </div>
            <div className="overview-card">
              <h3>Anzahl d. Einheiten</h3>
              <p>{portfolioNumUnits}</p>
            </div>
          </div>

          <div className="charts-container">
            <div className="card chart-left">
              <h2>Einnahmen & Ausgaben</h2>
              <div className="chart-wrapper">
                <StackedAreaChart data={portfolioCashflow} incomeLabel="Einnahmen" expensesLabel="Ausgaben" />
              </div>
            </div>
            <div className="card property-value-card chart-right">
              <h2>Immobilienwert in Euro</h2>
              <div className="value-comparison">
                <div className="value-box current">
                  <div className="value-label">Aktueller Wert</div>
                  <div className="value-amount">{formatEuro(portfolioValue.toFixed(0))}</div>
                  <div className="value-date">Bewertungsjahr: {getYearOnly(valuationDate)}</div>
                </div>
                <div className="value-change">
                  <div className="change-percentage">+{portfolioValueChangeIn}</div>
                  <div className="change-arrow">↑</div>
                </div>
                <div className="value-box purchase">
                  <div className="value-label">Kaufwert</div>
                  <div className="value-amount">{formatEuro(portfolioPurchasingValue.toFixed(0))}</div>
                  <div className="value-date">Kaufdatum: {avePurchasingDate || 'n/a'}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="card" style={{ marginTop: '20px' }}>
            <h2>Kreditübersicht</h2>
            <LoanOverview loans={transformCreditData(allLoans, selectedCustomerId)} />
          </div>
        </section>
      )}

      {/* === OBJEKT-ANSICHT === (nur wenn objectData.length > 0) */}
      {currentView === 'object' && objectData.length > 0 && (
        <section>
          <h2>Objekt-Ansicht</h2>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ marginRight: '8px' }}>Wähle Objekt:</label>
            <select
              value={selectedObjectId || ''}
              onChange={(e) => setSelectedObjectId(e.target.value)}
            >
              {objectData.map((o) => (
                <option key={o.objectId} value={o.objectId}>
                  {o.street} {o.number} ({o.bezeichnung})
                </option>
              ))}
            </select>
          </div>
          <div className="overview-cards">
            <div className="overview-card">
              <h3>Gesamtwert</h3>
              <p>{formatEuro(objectValue.toFixed(0))}</p>
            </div>
            <div className="overview-card">
              <h3>Letzte monatl. Einnahme</h3>
              <p>{formatEuro(objectLastMonthIncome.toFixed(0))}</p>
            </div>
            <div className="overview-card">
              <h3>Letzte monatl. Ausgabe</h3>
              <p>{formatEuro(objectLastMonthExpenses.toFixed(0))}</p>
            </div>
            <div className="overview-card">
              <h3>Vermietungsrate</h3>
              <p>{objectOccupancyRate}</p>
            </div>
            <div className="overview-card">
              <h3>Anzahl Einheiten</h3>
              <p>{objectUnits}</p>
            </div>
          </div>

          <div className="charts-container">
            <div className="card chart-left">
              <h2>Cashflow (12 Monate)</h2>
              <div className="chart-wrapper">
                <StackedAreaChart data={objectCashflowData} incomeLabel="Einnahmen" expensesLabel="Ausgaben" />
              </div>
            </div>
            <div className="card property-value-card chart-right">
              <h2>Immobilienwert in Euro</h2>
              <div className="value-comparison">
                <div className="value-box current">
                  <div className="value-label">Aktueller Wert</div>
                  <div className="value-amount">{formatEuro(objectValue.toFixed(0))}</div>
                  <div className="value-date">Bewertungsjahr: {getYearOnly(objectValuationDate)}</div>
                </div>
                <div className="value-change">
                  <div className="change-percentage">+{objectValueChangeIn}</div>
                  <div className="change-arrow">↑</div>
                </div>
                <div className="value-box purchase">
                  <div className="value-label">Kaufwert</div>
                  <div className="value-amount">{formatEuro(objectPurchasingValue.toFixed(0))}</div>
                  <div className="value-date">Kaufdatum: {objectPurchasingDate || 'n/a'}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="card" style={{ marginTop: '20px' }}>
            <h2>Kreditübersicht</h2>
            <LoanOverview
              loans={
                transformCreditData(allLoans, selectedCustomerId).filter(
                  loan =>
                    Array.isArray(loan.objectIds) && loan.objectIds.includes(selectedObjectId)
                )
              }
            />
          </div>
        </section>
      )}

      {/* === EINHEITEN-ANSICHT === */}
      {currentView === 'single' && singlePropertyData.length > 0 && (
        <section>
          <h2>Einheiten-Ansicht</h2>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ marginRight: '8px' }}>Wähle Einheit:</label>
            <select
              value={selectedSinglePropertyId || ''}
              onChange={(e) => setSelectedSinglePropertyId(e.target.value)}
            >
              {singlePropertyData.map((p) => (
                <option key={p.propertyId} value={p.propertyId}>
                  {p.street} {p.number} ({p.bezeichnung})
                </option>
              ))}
            </select>
          </div>
          <div className="overview-cards">
            <div className="overview-card">
              <h3>Gesamtwert</h3>
              <p>{formatEuro(singleValue.toFixed(0))}</p>
            </div>
            <div className="overview-card">
              <h3>Letzte monatl. Einnahme</h3>
              <p>{formatEuro(singleLastMonthIncome.toFixed(0))}</p>
            </div>
            <div className="overview-card">
              <h3>Letzte monatl. Ausgabe</h3>
              <p>{formatEuro(singleLastMonthExpenses.toFixed(0))}</p>
            </div>
            <div className="overview-card">
              <h3>Vermietungsrate</h3>
              <p>{singleOccupancy}</p>
            </div>
            <div className="overview-card">
              <h3>kumulierte Einnahmen</h3>
              <p>{formatEuro(singleFullYearIncome.toFixed(0))}</p>
            </div>
          </div>

          <div className="charts-container">
            <div className="card chart-left">
              <h2>Cashflow (12 Monate)</h2>
              <div className="chart-wrapper">
                <StackedAreaChart data={cashflowData} incomeLabel="Einnahmen" expensesLabel="Ausgaben" />
              </div>
            </div>
            <div className="card property-value-card chart-right">
              <h2>Immobilienwert in Euro</h2>
              <div className="value-comparison">
                <div className="value-box current">
                  <div className="value-label">Aktueller Wert</div>
                  <div className="value-amount">{formatEuro(singleValue.toFixed(0))}</div>
                  <div className="value-date">Bewertungsjahr: {getYearOnly(singleVDate)}</div>
                </div>
                <div className="value-change">
                  <div className="change-percentage">+{singleValueChangeIn}</div>
                  <div className="change-arrow">↑</div>
                </div>
                <div className="value-box purchase">
                  <div className="value-label">Kaufwert</div>
                  <div className="value-amount">{formatEuro(singlePurchasingValue.toFixed(0))}</div>
                  <div className="value-date">Kaufdatum: {singlePDate || 'n/a'}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="card" style={{ marginTop: '20px' }}>
            <h2>Kreditübersicht</h2>
            <LoanOverview
              loans={
                (() => {
                  const allFilteredLoans = transformCreditData(allLoans, selectedCustomerId);
                  const propLoans = allFilteredLoans.filter(loan =>
                    Array.isArray(loan.propertyIds) && loan.propertyIds.includes(selectedSinglePropertyId)
                  );
                  // Deduplicate by creditId
                  const uniqueLoans = Array.from(new Map(
                    propLoans.map(loan => [loan.creditId, loan])
                  ).values());
                  return uniqueLoans;
                })()
              }
            />
          </div>
        </section>
      )}
    </div>
  );
}
//test
export default Dashboard;